// Ringing a planet (docs/02-Game-Design/Core Mechanics.md, Planets). The word
// in progress is a tree of lines; one closing line between two of its stars
// makes exactly one loop. The loop rings the planet when the planet is inside
// it and nothing else is: no star of any kind, no other planet (and so no
// line). Of the loops that qualify, the one with the most stars wins, then
// the largest. Pure and deterministic, so the Worker's replay and the game's
// preview agree: the same taps always give the same closing line.
import { pointStrictlyInPolygon, polygonArea2 } from '../geometry/polygon';
import { dist2, pointWithinOfSegment, segmentsCross, type Point } from '../geometry/segments';
import type { LevelNode, Planet } from '../level/schema';
import { planetsOf } from '../level/schema';
import { blockRadius, derive, planetBlockRadius, type DerivedState } from './state';
import { treePath } from './tree';
import type { Line, PlayState } from './types';

export interface Enclosure {
  planetId: number;
  /** The stars on the loop, in path order from one end of the closing line to the other. */
  loop: number[];
  /** The line the game adds: [earlier selected, later selected]. */
  closing: Line;
  /** Twice the loop's area, for the tie-break. */
  area2: number;
}

/**
 * May the closing line a–b be drawn: crossing no line on the sky (one that
 * shares an end never crosses), passing over no used star but its ends (within
 * `radius` of one), and over no planet still in play (within `planetRadius`).
 */
export function closingLegal(
  nodes: ReadonlyMap<number, LevelNode>,
  a: number,
  b: number,
  lines: readonly Line[],
  used: ReadonlySet<number>,
  radius: number,
  planets: Iterable<Point>,
  planetRadius: number,
): boolean {
  const pa = nodes.get(a)!;
  const pb = nodes.get(b)!;
  for (const [la, lb] of lines) {
    if (la === a || lb === a || la === b || lb === b) continue;
    if (segmentsCross(pa, pb, nodes.get(la)!, nodes.get(lb)!)) return false;
  }
  for (const id of used) {
    if (id === a || id === b) continue;
    if (pointWithinOfSegment(nodes.get(id)!, pa, pb, radius)) return false;
  }
  for (const planet of planets) {
    if (pointWithinOfSegment(planet, pa, pb, planetRadius)) return false;
  }
  return true;
}

/** Most stars on the loop, then the largest loop, then the lowest-numbered ends. */
function better(a: Enclosure, b: Enclosure): boolean {
  if (a.loop.length !== b.loop.length) return a.loop.length > b.loop.length;
  const areaA = Math.abs(a.area2);
  const areaB = Math.abs(b.area2);
  if (areaA !== areaB) return areaA > areaB;
  const [a1, a2] = [Math.min(...a.closing), Math.max(...a.closing)];
  const [b1, b2] = [Math.min(...b.closing), Math.max(...b.closing)];
  return a1 !== b1 ? a1 < b1 : a2 < b2;
}

/**
 * The ring the word in progress would make on submission, or null: for every
 * planet still in play (not activated, not under a line), every pair of
 * selected stars not already joined whose closing line is legal, the loop
 * through the tree between them, kept when it holds that planet and nothing
 * else. The caller may pass `derive(state)` when it has it.
 */
export function enclosure(state: PlayState, derived: DerivedState = derive(state)): Enclosure | null {
  const { nodes, lines, used } = derived;
  const inPlay = planetsOf(state.level).filter((p) => !derived.planetsActivated.has(p.id) && !derived.planetsBlocked.has(p.id));
  if (inPlay.length === 0 || state.selection.length < 3) return null;
  const radius = blockRadius(state.level);
  const planetRadius = planetBlockRadius(state.level);
  const joined = new Set(state.selectionLines.map(([a, b]) => `${Math.min(a, b)}-${Math.max(a, b)}`));
  const others = state.level.nodes;
  let best: Enclosure | null = null;
  const sel = state.selection;
  for (let i = 0; i < sel.length; i++) {
    for (let j = i + 1; j < sel.length; j++) {
      const a = sel[i]!;
      const b = sel[j]!;
      if (joined.has(`${Math.min(a, b)}-${Math.max(a, b)}`)) continue;
      if (!closingLegal(nodes, a, b, lines, used, radius, inPlay, planetRadius)) continue;
      const loop = treePath(state.selectionLines, a, b);
      if (!loop || loop.length < 3) continue;
      const poly = loop.map((id) => nodes.get(id)!);
      const onLoop = new Set(loop);
      for (const planet of inPlay) {
        if (!pointStrictlyInPolygon(planet, poly)) continue;
        if (!ringsOnly(planet, poly, onLoop, others, inPlay)) continue;
        const candidate: Enclosure = { planetId: planet.id, loop, closing: [a, b], area2: polygonArea2(poly) };
        if (!best || better(candidate, best)) best = candidate;
      }
    }
  }
  return best;
}

/** Nothing but this planet inside the loop: no star off the loop, no other planet. */
function ringsOnly(planet: Planet, poly: readonly Point[], onLoop: ReadonlySet<number>, nodes: readonly LevelNode[], planets: readonly Planet[]): boolean {
  for (const n of nodes) {
    if (onLoop.has(n.id)) continue;
    if (pointStrictlyInPolygon(n, poly)) return false;
  }
  for (const p of planets) {
    if (p.id === planet.id) continue;
    if (pointStrictlyInPolygon(p, poly)) return false;
  }
  return true;
}

/** Is the point within the planet's spacing of any of these? For the designer and generator. */
export function nearPlanet(p: Point, planets: Iterable<Point>, spacing: number): boolean {
  const s2 = spacing * spacing;
  for (const q of planets) if (dist2(p, q) < s2) return true;
  return false;
}
